<template>
  <div class="chart-period-selector" :class="classes">
    <div class="v-centerer">
      <div class="normal-text presets-wrapper">
        <IndicatorDisplayConfigField
          v-if="!hidePresetField"
          :value="selectedPresetId"
          :disabled="disabled"
          class="presets"
          :class="{ 'greyed-out': disabled }"
          legend=""
          :show-default-wheel-view="showDefaultWheelView"
          @input="presetIdPickedFromRadio"
          @startEdit="$emit('presetEditorOpened')"
          @close="$emit('presetEditorClosed')"
        />
      </div>
      <div
        v-if="!hideDates"
        v-quick-title="disableDateSwitcher ? $t('message.DATE_SWITCHER_DISABLED') : ''"
        class="title-range h-v-centerer generic-border generic-border--small-text-btn"
        :class="{ 'title-range--disabled': disableDateSwitcher }"
      >
        <div class="title-range__arrow generic-border--small-text-btn h-v-centerer" @click="rangeMinus">
          <Icon>{{ arrowDownLine }}</Icon>
        </div>
        <div class="current-range generic-border--small-text-btn" @click="editDate">
          {{ currentRange }}
          <input ref="dpEdit" class="chart-period-selector__dp-field">
        </div>
        <div class="title-range__arrow generic-border--small-text-btn h-v-centerer" @click="rangePlus">
          <Icon>{{ arrowDownLine }}</Icon>
        </div>
      </div>
    </div>
    <TransitionExpand>
      <div v-if="detailsExpanded" class="details">
        <!-- Dynamic editable ranges container -->
        <div class="left-section">
          <div class="dynamic-ranges-values">
            <div class="dynamic-ranges">
              <!-- Dynamic visual period of a point -->
              <div class="dynamic-range">
                <img
                  :src="MINUS_MEDIUM_IMG"
                  alt=""
                  @click="setPointPeriod({ period: pointPeriod - 1 })"
                >
                <div
                  ref="pointPeriod"
                  :style="pointPeriodStyle"
                />
                <img
                  :src="PLUS_MEDIUM_IMG"
                  alt=""
                  @click="setPointPeriod({ period: pointPeriod + 1 })"
                >
              </div>
              <div class="field-hint">
                {{ $t('message.CHART_POINT_PERIOD') }}
              </div>
              <!-- Dynamic visual period of charts -->
              <div class="dynamic-range mt5">
                <img
                  :src="MINUS_MEDIUM_IMG"
                  alt=""
                  @click="setChartPeriod({ period: chartPeriod - 1 })"
                >
                <div ref="chartPeriod" />
                <img
                  :src="PLUS_MEDIUM_IMG"
                  alt=""
                  @click="setChartPeriod({ period: chartPeriod + 1 })"
                >
              </div>
              <div class="field-hint">
                {{ $t('message.VISIBLE_CHART_PERIOD') }}
              </div>
            </div>
            <div class="range-values">
              <!-- Fields to edit the points period -->
              <div class="range-value">
                <InputNumber
                  :value="pointPeriod"
                  :min="1"
                  @input="setPointPeriod({ period: $event })"
                />
                <EditableLabelRadio
                  :value="pointPeriodUnitIdx"
                  class="ml5"
                  :items="periodItems(pointPeriod)"
                  label-key="unit"
                  @input="setPointPeriod({ unit: $event })"
                />
              </div>
              <!-- Fields to edit the charts period -->
              <div class="range-value">
                <InputNumber
                  :value="chartPeriod"
                  :min="1"
                  @input="setChartPeriod({ period: $event })"
                />
                <EditableLabelRadio
                  :value="chartPeriodUnitIdx"
                  class="ml5"
                  :items="periodItems(chartPeriod)"
                  label-key="unit"
                  @input="setChartPeriod({ unit: $event })"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="right-section">
          <EditableLabelRadio
            v-if="pointPeriodUnitIdx === 1"
            :value="pointStartDay - 1"
            :items="weekDays"
            :legend="`${$t('message.PERIOD_START_DAY')}${$t('message.TWO_DOTS')}`"
            @input="pointStartDay = $event + 1"
          />
          <InputNumber
            v-else-if="pointPeriodUnitIdx === 2"
            v-model="pointStartDay"
            class="start-day-field"
            :min="1"
            :max="31"
            :legend="`${$t('message.PERIOD_START_DAY')}${$t('message.TWO_DOTS')}`"
            :size="4"
            refresh-after-input
          />
          <br>
          <button class="action-btn" @click="displayConfigEditor = true">
            {{ $t('message.MANAGE_PRESETS') }}
          </button>
        </div>
      </div>
    </TransitionExpand>
    <!-- TODO: Consider removing increase-disabled -->
    <IndicatorDisplayConfig
      :displayed="displayConfigEditor"
      advanced-mode
      @select="presetIdPickedFromRadio($event.id)"
      @close="displayConfigEditor = false"
    />
  </div>
</template>

<script>
/**
 * H1 title with a range and period selector
 *
 * @vue-prop {Object} range - Object containing a 'start' and 'end' attributes
 * @vue-prop {Number} presetId - Selected preset if any
 * @vue-prop {Array} workingDays - Working days to take into account when moving the range.
 *                                 If not specified, use the current logged team's working days
 * @vue-prop {String} [queryPrefix] - As of v1.7.15, query is managed directly in this component instead of the pages
 *                                    That prevents redundancy in the code and improves maintainability.
 * @vue-prop {Boolean} showDefaultWheelView - Whether the option to display wheels should be available or not
 * @vue-prop {Boolean} [hidePresetField] - Hide the preset selection button
 * @vue-prop {Boolean} [hideDates] - Hide the dates selection button
 */

import { isEqual } from 'lodash';
import $ from 'jquery';
// State
import { mapState } from 'vuex';
import { FETCH_INDICATOR_DISPLAY_CONFIGS } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS } from '@/store/mutation-types';
// Components
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import IndicatorDisplayConfig from '@/components/indicator-display-config.vue';
import IndicatorDisplayConfigField from '@/components/indicator-display-config-field.vue';
import InputNumber from '@/components/input-number.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Utils
import { calcPeriodStartDate, PERIOD_UNITS_DATA } from '@/utils/main';
import { arrowDownLine } from '@/assets/icons/';


/** Timeout in milliseconds to emit 'input'. See emitInput method */
const RPT_INPUT_TIMEOUT = 300;
const MINUS_MEDIUM_IMG = require('@/assets/img/minus-circle-medium-gray.png');
const PLUS_MEDIUM_IMG = require('@/assets/img/plus-circle-medium-gray.png');


export default {

  name: 'ChartPeriodSelector',

  components: {
    EditableLabelRadio,
    Icon,
    IndicatorDisplayConfig,
    IndicatorDisplayConfigField,
    InputNumber,
    TransitionExpand,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    presetId: {
      type: Number,
      default: 1,
    },
    expandable: {
      type: Boolean,
      default: true,
    },
    workingDays: {
      type: Array,
      default: undefined,
    },
    instantUpdate: Boolean,
    disableDateSwitcher: Boolean,
    defaultExpanded: Boolean,
    disabled: Boolean,
    hidePresetField: Boolean,
    hideDates: Boolean,
    queryPrefix: {
      type: String,
      default: '',
    },
    showDefaultWheelView: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      MINUS_MEDIUM_IMG,
      PLUS_MEDIUM_IMG,
      // Whether the periods section is visible or not
      displayConfigEditor: false,
      detailsExpanded: false,
      plusMinusWidth: 25,
      sizeUpdatePending: false,
      // Timeout to emit the new chart range (to prevent repeated updates)
      emitInputTimeout: null,
      // Change display when size is too small
      smallSize: false,
      // All query params set since this component is created
      allQueryKeys: [
        'pointPeriod',
        'pointPeriodUnitIdx',
        'pointStartDay',
        'chartPeriod',
        'chartPeriodUnitIdx',
        'chartPeriodEndDate',
      ],
      datepickerEndDate: null,
    };
  },

  computed: {
    classes() {
      return {
        small: this.smallSize,
        'not-expandable': !this.expandable,
      };
    },
    /** Number: of days in the point period. Used for the width of the periods selectors */
    pointPeriodDays() {
      return this.convertPeriodToDays(this.computedValue.pointPeriodObj);
    },
    /** Number: of days in the charts period. Used for the width of the periods selectors */
    chartPeriodDays() {
      return this.convertPeriodToDays(this.computedValue.chartPeriodObj);
    },
    /** Style of the point period (width depends on the ratio pointPeriod / chartPeriod) */
    pointPeriodStyle() {
      const ratio = this.pointPeriodDays / this.chartPeriodDays;
      return {
        width: `calc(${ratio * 100}% - ${2 * this.plusMinusWidth}px)`,
      };
    },
    /** Period choices in EditableLabelRadio */
    pointPeriodItems() {
      return this.periodItems(this.pointPeriod);
    },
    /** Period choices in EditableLabelRadio */
    chartPeriodItems() {
      return this.periodItems(this.chartPeriod);
    },
    /** Moment start date of the dynamic date picker */
    datepickerStartDate() {
      const { chartPeriodObj, pointPeriodObj } = this.computedValue;
      return calcPeriodStartDate(this.datepickerEndDate, chartPeriodObj, pointPeriodObj);
    },
    /** Moment: Start date of the current range */
    startDate() {
      return calcPeriodStartDate(this.endDate, this.computedValue.chartPeriodObj, this.computedValue.pointPeriodObj);
    },
    /** Displayed range at the top right */
    currentRange() {
      if (!this.endDate) {
        return '';
      }
      let df = this.$t('fullDateFormat');
      if (this.$store.state.device.isSmartphonePortrait) {
        df = this.$t('yyDateFormat');
      }
      if (this.endDate.isSame(this.startDate)) {
        return this.endDate.format(df);
      }
      return `${this.startDate.format(df)} - ${this.endDate.format(df)}`;
    },
    allPresets() {
      return [
        ...this.$store.getters.formIndicDisplayConfigs,
        this.$store.getters.formWheelDisplayConfig,
        ...this.customConfigs.map(item => ({
          ...item,
          pointPeriodObj: { period: item.pointPeriod, unit: item.pointUnit, startDay: item.pointStartDay },
          chartPeriodObj: { period: item.chartPeriod, unit: item.chartUnit },
        })),
      ];
    },
    /** Object: Currently selected preset */
    selectedPreset() {
      return this.allPresets.find(p => p && p.id === this.selectedPresetId)
        || this.$store.getters.defaultIndicDisplayConfig;
    },
    /** Boolean: Whether the current preset has a fixed end date or not */
    endDateIsFixed() {
      return this.selectedPreset && this.selectedPreset.chartEndDate !== null;
    },
    /** Available choices when picking startDay if unit is 'week' */
    weekDays() {
      return this.$t('weekDays').map((weekDay, i) => ({ id: i, label: weekDay }));
    },
    /** Array: Working days. If prop 'workingDays' isn't specified, team's working days */
    compWorkingDays() {
      const wd = this.workingDays || this.currentTeam.workingDays;
      if (wd.every(day => !day)) {
        return [true, true, true, true, true, true, true];
      }
      return this.workingDays || this.currentTeam.workingDays;
    },
    // Custom configs
    ...mapState({
      customConfigs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      customConfigsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),

    // ============
    // Query params
    // ============
    computedValue() {
      return {
        ...this.selectedPreset,
        pointPeriodObj: {
          period: this.pointPeriod,
          unit: this.pointPeriodItems[this.pointPeriodUnitIdx].letter,
          startDay: this.pointStartDay,
        },
        chartPeriodObj: {
          period: this.chartPeriod,
          unit: this.chartPeriodItems[this.chartPeriodUnitIdx].letter,
        },
        chartEnd: this.endDate,
      };
    },
    pointPeriod: {
      get() {
        return Number(this.$route.query[`${this.queryPrefix}pointPeriod`] || this.selectedPreset.pointPeriodObj.period);
      },
      set(newVal) {
        if (Number(newVal) !== Number(this.selectedPreset.pointPeriodObj.period)) {
          this.setToQuery({ pointPeriod: String(Number(newVal)) });
        } else {
          this.setToQuery({ pointPeriod: undefined });
        }
      },
    },
    pointPeriodUnitIdx: {
      get() {
        if (this.$route.query[`${this.queryPrefix}pointPeriodUnitIdx`] !== undefined) {
          return Number(this.$route.query[`${this.queryPrefix}pointPeriodUnitIdx`]) % this.pointPeriodItems.length;
        }
        return this.pointPeriodItems.findIndex(item => item.letter === this.selectedPreset.pointPeriodObj.unit);
      },
      set(newVal) {
        if (this.$route.query[`${this.queryPrefix}pointPeriodUnitIdx`] !== undefined) {
          if (Number(this.$route.query[`${this.queryPrefix}pointPeriodUnitIdx`]) !== newVal) {
            this.setToQuery({ pointPeriodUnitIdx: newVal });
          }
        } else {
          const { letter } = this.pointPeriodItems[newVal];
          if (letter !== this.selectedPreset.pointPeriodObj.unit) {
            this.setToQuery({ pointPeriodUnitIdx: newVal });
          }
        }
      },
    },
    pointStartDay: {
      get() {
        const defaultVal = this.selectedPreset.pointPeriodObj.startDay;
        return Number(this.$route.query[`${this.queryPrefix}pointStartDay`] || defaultVal);
      },
      set(newVal) {
        if (Number(newVal) !== Number(this.selectedPreset.pointPeriodObj.startDay)) {
          this.setToQuery({ pointStartDay: String(Number(newVal)) });
        }
      },
    },
    chartPeriod: {
      get() {
        return Number(this.$route.query[`${this.queryPrefix}chartPeriod`]
          || this.selectedPreset.chartPeriodObj.period);
      },
      set(newVal) {
        if (Number(newVal) !== Number(this.selectedPreset.chartPeriodObj.period)) {
          this.setToQuery({ chartPeriod: String(Number(newVal)) });
        } else {
          this.setToQuery({ chartPeriod: undefined });
        }
      },
    },
    chartPeriodUnitIdx: {
      get() {
        if (this.$route.query[`${this.queryPrefix}chartPeriodUnitIdx`] !== undefined) {
          return Number(this.$route.query[`${this.queryPrefix}chartPeriodUnitIdx`]) % this.chartPeriodItems.length;
        }
        return this.chartPeriodItems.findIndex(item => item.letter === this.selectedPreset.chartPeriodObj.unit);
      },
      set(newVal) {
        if (this.$route.query[`${this.queryPrefix}chartPeriodUnitIdx`] !== undefined) {
          if (Number(this.$route.query[`${this.queryPrefix}chartPeriodUnitIdx`]) !== newVal) {
            this.setToQuery({ chartPeriodUnitIdx: newVal });
          }
        } else {
          const { letter } = this.chartPeriodItems[newVal];
          if (letter !== this.selectedPreset.chartPeriodObj.unit) {
            this.setToQuery({ chartPeriodUnitIdx: newVal });
          }
        }
      },
    },
    endDate: {
      get() {
        const date = this.$route.query[`${this.queryPrefix}chartPeriodEndDate`];
        if (date) {
          return this.$moment(date);
        }
        const { dateGapFromCurrent, dateGapUnit } = this.selectedPreset;
        if (dateGapFromCurrent && dateGapUnit) {
          return this.doMinusPeriod(dateGapFromCurrent, dateGapUnit, 1, this.$moment());
        }
        return this.$moment();
      },
      set(newVal) {
        this.setToQuery({ chartPeriodEndDate: newVal.format('YYYY-MM-DD') });
      },
    },
    selectedPresetId: {
      get() {
        return Number(this.$route.query[`${this.queryPrefix}selectedPresetId`] || this.presetId);
      },
      set(newVal) {
        this.setToQuery({ selectedPresetId: String(Number(newVal)) });
      },
    },
  },

  watch: {
    presetId() {
      // Preset id was set from the parent
      this.clearQuery();
      this.selectedPresetId = this.presetId;
    },
    computedValue: {
      deep: true,
      handler(newVal) {
        clearTimeout(this.emitInputTimeout);
        if (!this.instantUpdate) {
          this.emitInputTimeout = setTimeout(() => {
            this.$emit('input', newVal);
          }, RPT_INPUT_TIMEOUT);
        } else {
          this.$emit('input', newVal);
        }
      },
    },
  },

  created() {
    this.updateChartPeriod();
    // Fetch any custom configs
    if (!this.customConfigsLoading && this.currentTeam) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }
    this.selectedPresetId = this.presetId;
  },

  mounted() {
    this.sizeUpdate();
    $(window).on(this.resizeEventName, () => {
      this.sizeUpdatePending = true;
      this.sizeUpdate();
    });
    if (this.detailsExpanded !== this.defaultExpanded) {
      this.detailsExpanded = !this.detailsExpanded;
    }
  },

  updated() {
    if (this.sizeUpdatePending) {
      this.sizeUpdatePending = false;
      this.sizeUpdate();
    }
  },

  methods: {
    /**
     * User chose a preset amongst the default or custom presets
     * @param {Number} newVal - preset ID
     */
    presetIdPickedFromRadio(newVal) {
      this.$emit('presetPick', newVal);
      this.clearQuery();
    },
    /**
     * Return the number of days of a period
     * @param  {Object} periodObj - object containing a 'period' and 'unit' key
     * @return {Number} - Number of days in the period
     */
    convertPeriodToDays(periodObj) {
      if (periodObj.unit === 'w') {
        return periodObj.period * 7;
      }
      if (periodObj.unit === 'M') {
        return periodObj.period * 30;
      }
      return periodObj.period;
    },
    /**
     * Returns the available period units
     * @param  {Number} period - Number of units
     * @return {Array} - Array of period units formatted as { id, letter (moment), unit (plain text) }
     */
    periodItems(period) {
      return PERIOD_UNITS_DATA.map(unit => ({ ...unit, unit: this.$tc(unit.unitTr, period) }));
    },
    /** Chart period unit edited */
    setPointPeriod(val) {
      if (typeof val.unit !== 'undefined') {
        // Reset start day when changing unit
        if (val.unit !== this.pointPeriodUnitIdx) {
          this.pointStartDay = 1;
        }
        this.pointPeriodUnitIdx = val.unit;
      }
      if (typeof val.period !== 'undefined') {
        this.pointPeriod = Number(val.period);
      }
      // update the chart period so it remains bigger than the point period
      this.updateChartPeriod();
    },
    /** Chart period unit edited */
    setChartPeriod(val) {
      if (typeof val.unit !== 'undefined') {
        this.chartPeriodUnitIdx = val.unit;
      }
      if (typeof val.period !== 'undefined') {
        this.chartPeriod = Number(val.period);
      }
      // update the point period so it remains below chart period
      this.updatePointPeriod();
    },
    /** Update the charts period if it is shorter than the point period */
    updateChartPeriod() {
      const chartPeriodDays = this.convertPeriodToDays(this.computedValue.chartPeriodObj);
      const pointPeriodDays = this.convertPeriodToDays(this.computedValue.pointPeriodObj);
      if (chartPeriodDays < pointPeriodDays) {
        this.chartPeriod = this.pointPeriod;
        this.chartPeriodUnitIdx = this.pointPeriodUnitIdx;
      }
    },
    /** Update the point period if it is bigger than the chart period */
    updatePointPeriod() {
      const chartPeriodDays = this.convertPeriodToDays(this.computedValue.chartPeriodObj);
      const pointPeriodDays = this.convertPeriodToDays(this.computedValue.pointPeriodObj);
      if (chartPeriodDays < pointPeriodDays) {
        // Reset start day if unit chnages
        if (this.chartPeriodUnitIdx !== this.pointPeriodUnitIdx) {
          this.pointStartDay = 1;
        }
        this.pointPeriod = this.chartPeriod;
        this.pointPeriodUnitIdx = this.chartPeriodUnitIdx;
      }
    },
    /** Size changed. Adapt the component measures */
    sizeUpdate() {
      // Adapt CSS rules to the size of the root element
      const container = this.$el;
      const textEl = this.$refs.text;
      if (container && textEl) {
        this.smallSize = container.offsetWidth - textEl.offsetWidth < 650;
      }
    },
    /** Shifts the entire range backward. Behaves differently according to the unit */
    rangeMinus() {
      if (!this.disableDateSwitcher && !this.endDateIsFixed) {
        const { unit, startDay } = this.computedValue.pointPeriodObj;
        this.endDate = this.doMinusPeriod(1, unit, startDay, this.endDate);
      }
    },
    /** Shifts the entire range forward */
    rangePlus() {
      if (!this.disableDateSwitcher && !this.endDateIsFixed) {
        const { unit, startDay } = this.computedValue.pointPeriodObj;
        this.endDate = this.doPlusPeriod(1, unit, startDay, this.endDate);
      }
    },
    doMinusPeriod(number, unit, startDay, initialDate) {
      let tmpDate = this.$moment(initialDate);
      if (unit === 'd') {
        // Day unit, keep shifting until reaching a working day
        tmpDate.subtract(number, unit);
        while (!this.compWorkingDays[tmpDate.isoWeekday() - 1]) {
          tmpDate.subtract(1, unit);
        }
      } else {
        tmpDate.subtract(number - 1, unit);
        // 1: Get to the start day of the current period
        if (unit === 'w') {
          const newDate = this.$moment(tmpDate).day(startDay % 7);
          tmpDate = newDate.isAfter(tmpDate) ? newDate.subtract(1, 'w') : newDate;
        } else if (unit === 'M') {
          tmpDate.date(startDay);
        }
        // 2: Get to the last day of the period to display
        tmpDate.subtract(1, 'd');
      }
      return tmpDate;
    },
    doPlusPeriod(number, unit, startDay, initialDate) {
      let tmpDate = this.$moment(initialDate);
      if (unit === 'd') {
        // Day unit, keep shifting until reaching a working day
        tmpDate.add(number, unit);
        while (!this.compWorkingDays[tmpDate.isoWeekday() - 1]) {
          tmpDate.add(1, unit);
        }
      } else {
        // 1: Shifts two units forward
        tmpDate.add(number + 1, unit);
        // 2: Get to the start day of the next period
        if (unit === 'w') {
          const newDate = this.$moment(tmpDate).day(startDay % 7);
          tmpDate = newDate.isAfter(tmpDate) ? newDate.subtract(1, 'w') : newDate;
        } else if (unit === 'M') {
          tmpDate.date(startDay);
        }
        // 3: Get to the last day of the period to display
        tmpDate.subtract(1, 'd');
      }
      return tmpDate;
    },
    /**
     * Trigger the date picker to update the range.
     */
    editDate() {
      this.datepickerEndDate = this.$moment(this.endDate);
      const $inputEl = $(this.$refs.dpEdit);
      $inputEl.val(this.datepickerEndDate.format('YYYY-MM-DD'));
      $inputEl.datepicker('destroy');
      $inputEl.datepicker({
        classes: 'shizen-datepicker chart-period-selector-dp',
        changeMonth: true,
        changeYear: true,
        showButtonPanel: true,
        showOtherMonths: true,
        selectOtherMonths: true,
        nextText: this.$t('message.NEX'),
        prevText: this.$t('message.PREV'),
        currentText: this.$t('message.TODAY_SHORT'),
        closeText: this.$t('message.DONE'),
        dateFormat: 'yy-mm-dd',
        defaultDate: this.datepickerEndDate.format('YYYY-MM-DD'),
        beforeShowDay: (date) => {
          // Set a custom class to values in the range to display the range properly
          let customClass = '';
          if (this.datepickerStartDate.isSame(date, 'd') || this.datepickerEndDate.isSame(date, 'd')) {
            customClass = 'ui-datepicker-currentrange';
            if (this.datepickerStartDate.isSame(date, 'd')) {
              customClass += ' ui-datepicker-currentrange--start';
            } else {
              customClass += ' ui-datepicker-currentrange--end';
            }
          }
          if (this.datepickerStartDate.isSameOrBefore(date, 'd') && this.datepickerEndDate.isSameOrAfter(date, 'd')) {
            customClass += ' ui-datepicker-inrange';
          }
          customClass += ` shizen-datepicker__day ${this.$moment(date).format('YYYY-MM-DD')}`;
          return [true, customClass, null];
        },
        beforeShow: (input, inst) => {
          inst.dpDiv.addClass('chart-period-selector__date-picker');
        },
        onClose: (input, inst) => {
          inst.dpDiv.removeClass('chart-period-selector__date-picker');
        },
        onSelect: (dateText, inst) => {
          this.datepickerEndDate.set('year', inst.selectedYear);
          this.datepickerEndDate.set('month', inst.selectedMonth);
          this.datepickerEndDate.set('date', inst.selectedDay);
          this.endDate = this.datepickerEndDate;
        },
        onChangeMonthYear: () => {
          this.$nextTick(this.watchDatepickerHover);
        },
      });
      $inputEl.datepicker('show');
      this.watchDatepickerHover();
    },
    /** Liste to hovering events on datepicker days to visually update it */
    watchDatepickerHover() {
      $('.ui-datepicker-calendar .shizen-datepicker__day').on('mouseenter', (e) => {
        if (e && e.currentTarget && e.currentTarget.className) {
          const dateClass = e.currentTarget.className
            .split(' ')
            .find(cssClass => cssClass.match(/\d{4}-\d{2}-\d{2}/));
          if (dateClass) {
            this.datepickerEndDate = this.$moment(dateClass);
            // Wait nextTick for datepickerStartDate to update
            this.$nextTick(this.updateCalendarFocusedDates);
          }
        }
      });
    },
    /** When a datepicker day is hovered, change the display to illustrate how the range would look like */
    updateCalendarFocusedDates() {
      $('.ui-datepicker-calendar .shizen-datepicker__day').each((idx, elt) => {
        if (elt && elt.className) {
          const dateClass = elt.className.split(' ').find(cssClass => cssClass.match(/\d{4}-\d{2}-\d{2}/));
          if (dateClass) {
            const md = this.$moment(dateClass);
            let customClass = '';
            if (this.datepickerStartDate.isSame(md, 'd') || this.datepickerEndDate.isSame(md, 'd')) {
              customClass = 'ui-datepicker-currentrange';
              if (this.datepickerStartDate.isSame(md, 'd')) {
                customClass += ' ui-datepicker-currentrange--start';
              } else {
                customClass += ' ui-datepicker-currentrange--end';
              }
            }
            if (this.datepickerStartDate.isSameOrBefore(md, 'd') && this.datepickerEndDate.isSameOrAfter(md, 'd')) {
              customClass += ' ui-datepicker-inrange';
            }
            $(elt).removeClass('ui-datepicker-currentrange ui-datepicker-inrange');
            $(elt).removeClass('ui-datepicker-currentrange--start ui-datepicker-currentrange--end');
            $(elt).addClass(customClass);
          }
        }
      });
    },
    /** Everything below the title can be (un-)expanded */
    expandDetails() {
      this.detailsExpanded = !this.detailsExpanded;
      setTimeout(() => { this.$emit('expand', this.detailsExpanded); }, 150);
    },
    /** Update query params */
    setToQuery(params) {
      const query = Object.assign({}, this.$route.query);
      Object.keys(params).forEach((key) => {
        query[`${this.queryPrefix}${key}`] = params[key];
      });
      if (!isEqual(this.$route.query, query)) {
        this.$router.replace({ query });
      }
    },
    clearQuery() {
      const query = Object.assign({}, this.$route.query);
      this.allQueryKeys.forEach((key) => {
        delete query[`${this.queryPrefix}${key}`];
      });
      if (!isEqual(this.$route.query, query)) {
        this.$router.replace({ query });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.date-picker {
  display: flex;
  align-items: center;
}

.chart-period-selector {
  $arrow-size: 25px;

  .presets-wrapper {
    text-align: left;

    .indicator-display-config-field {
      &__editable-label {
        .editable-label-text {
          max-width: 15rem;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
      }
    }
  }
  &.not-expandable .presets-wrapper {
    top: 17px;
  }
  .presets {
    max-width: 100%;
  }

  .details {
    height: 0;
    overflow: hidden;
  }
  .left-section, .right-section {
    display: inline-block;
    vertical-align: top;
    padding: 3px 10px 8px 10px;
    box-sizing: border-box;
  }
  .left-section {
    width: 60%;
  }
  .right-section {
    width: 40%;
  }

  .dynamic-ranges-values {
    display: flex;
    flex-direction: row;

    .dynamic-ranges {
      flex: 1;

      .dynamic-range {
        position: relative;
        flex: 1;

        > * {
          display: inline-block;
          vertical-align: middle;
        }
        > img {
          width: $arrow-size;
          height: auto;
          cursor: pointer;
        }
        > div {
          height: 0.1em;
          width: calc(100% - #{$arrow-size * 2});
          background: #414042;
        }
      }
    }
    .range-values {
      margin-left: 5px;

      .range-value {
        padding: 5px 2px;

        input, .editable-label {
          border: 1px gray dashed;
          padding: 0px 5px;
          line-height: 1.5em;
          background: #fff;
          font-size: 1em;
        }
        input {
          width: 30px;
          vertical-align: middle;
          padding: 0.5px 5px;
        }
        .editable-label {
          display: inline-block;
          vertical-align: middle;
        }
      }
    }
  }
  .range-value .editable-label, .start-day-field input {
    border: 1px gray dashed;
    padding: 0px 5px;
    line-height: 1.5em;
    background: #fff;
    font-size: 1em;
  }
  .periods-expand {
    cursor: pointer;
    padding: 1px 10px;
    font-size: 0.9em;
    border-radius: 50px;
    background: rgba(43, 76, 255, 0.15);
    display: inline-block;

    &:hover {
      background-color: rgba(43, 76, 255, 0.3);
    }
    > * {
      display: inline-block;
      vertical-align: middle;
    }
    img {
      width: 15px;
      height: auto;
      margin-right: 5px;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .title-range {
    margin: 0px 16px;

    &.generic-border--small-text-btn {
      padding: 0;

      &:hover {
        background: $color-gray-background;
      }
    }

    .current-range {
      position: relative;
      justify-content: center;
      white-space: nowrap;
      cursor: pointer;

      &.generic-border--small-text-btn {
        padding: 0 0.5rem;
      }
    }

    &__arrow {
      width: $small-button-height;
      height: $small-button-height;

      &.generic-border--small-text-btn {
        padding: 0;
      }

      .icon {
        margin: auto;
        width: .8rem;
        height: .8rem;
      }

      &:first-child .icon {
        transform: rotate(90deg);
      }

      &:last-child .icon {
        transform: rotate(270deg);
      }
    }

    .generic-border--icon-button:first-child .icon {
      transform: rotate(90deg);
    }

    .generic-border--icon-button:last-child .icon {
      transform: rotate(270deg);
    }

    &--disabled {
      opacity: 0.15;
      cursor: default !important;
    }
  }

  &.small {
    .basic-blue-title {
      padding: 3px 0px
    }
    .presets-wrapper {
      position: static;
      display: flex;
      max-width: 100%;
      width: auto;
    }
    .presets-wrapper, div.title-range {
      font-size: 11px;
      padding: 0px 4px;
      float: initial;
    }
    .presets-wrapper, .title-range span {
      justify-content: center;
    }
    .title {
      display: none;
    }
  }

  &__dp-field {
    position: absolute;
    bottom: 0;
    left: 0;
    visibility: hidden;
    width: 0px;
    height: 0px;
  }
}

@media (max-width: $smartphone-portrait) {
  .chart-period-selector {

    .title-range {
      margin: 0 .5rem;
    }
  }
}
</style>
